import React, { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { TurningMark } from "@/components/brand";
import { Button } from "@/components/ui/button";

type Props = {
  isOpen: boolean;
  onReload: () => void;
};

/**
 * Blocking "Update Required" prompt for a build the running one can't keep
 * working beside (version.json `breaking` or `minBuiltAt`), drawn like the
 * app's bottom-center toast. It stays an AlertDialog so the page behind
 * can't be used on the stale build: no close via Escape or the overlay,
 * focus held on Reload.
 */
export const ForceReloadModal: React.FC<Props> = ({ isOpen, onReload }) => {
  const [reloading, setReloading] = useState(false);
  const reloadRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent
        // AlertDialog only auto-focuses an AlertDialogCancel; without one,
        // focus would stay on the page behind.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          reloadRef.current?.focus();
        }}
        onEscapeKeyDown={(event) => event.preventDefault()}
        className="top-auto bottom-6 z-6000 flex w-96 translate-y-0 items-center gap-3 rounded-lg p-4 data-open:slide-in-from-bottom-2"
      >
        <TurningMark turning="loop" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <AlertDialogTitle>Update Required</AlertDialogTitle>
          <AlertDialogDescription>
            Reload to keep working. Your drafts are kept.
          </AlertDialogDescription>
        </div>
        <Button
          ref={reloadRef}
          variant="primary"
          disabled={reloading}
          aria-busy={reloading || undefined}
          onClick={() => {
            setReloading(true);
            onReload();
          }}
        >
          Reload
        </Button>
      </AlertDialogContent>
    </AlertDialog>
  );
};
